/* Layout — sidebar + header + main content area.
 *
 * Consumes <BrandGlyph> from design-system-react. Tenant branding is
 * applied via applyTenantBranding() on session boot (called from
 * the dashboard load — TODO: move to a dedicated session-bootstrap
 * hook once we wire /me).
 *
 * Routes that render inside this layout render into the <Outlet>. */

import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { BrandGlyph, Button } from '@sp/design-system-react';
import { LayoutDashboard, Cpu, Bell, LogOut } from 'lucide-react';
import { useAuth } from '../lib/auth';

interface NavItem {
  to: string;
  label: string;
  icon: typeof LayoutDashboard;
}

const NAV_ITEMS: NavItem[] = [
  { to: '/', label: 'Dashboard', icon: LayoutDashboard },
  { to: '/devices', label: 'Devices', icon: Cpu },
  { to: '/alerts', label: 'Alerts', icon: Bell },
];

export function Layout(): JSX.Element {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();

  const handleSignOut = async (): Promise<void> => {
    await signOut();
    navigate('/login', { replace: true });
  };

  return (
    <div className="flex h-full w-full">
      {/* Sidebar */}
      <aside
        className="flex w-60 flex-col gap-2 border-r p-4"
        style={{ borderColor: 'var(--border-hairline)', background: 'var(--bg-surface)' }}
      >
        <div className="mb-6 flex items-center gap-3">
          <BrandGlyph size={32} coral alt="SiliconProof AQ" />
          <div className="flex flex-col">
            <span className="text-sm font-semibold" style={{ color: 'var(--ink-primary)' }}>
              AQ Monitor
            </span>
            <span className="text-[11px] uppercase tracking-wider" style={{ color: 'var(--ink-tertiary)' }}>
              SiliconProof
            </span>
          </div>
        </div>

        <nav className="flex flex-col gap-1">
          {NAV_ITEMS.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === '/'}
              className={({ isActive }) =>
                `flex items-center gap-3 rounded-md px-3 py-2 text-sm transition-colors ${
                  isActive ? 'font-medium' : ''
                }`
              }
              style={({ isActive }) => ({
                background: isActive ? 'var(--bg-sunken)' : 'transparent',
                color: isActive ? 'var(--ink-primary)' : 'var(--ink-secondary)',
              })}
            >
              <item.icon size={16} aria-hidden />
              {item.label}
            </NavLink>
          ))}
        </nav>

        <div className="mt-auto flex flex-col gap-2">
          {user && (
            <div className="flex flex-col gap-0.5 px-3 py-2">
              <span className="text-sm" style={{ color: 'var(--ink-primary)' }}>
                {user.name ?? user.email ?? 'Signed in'}
              </span>
              <span className="text-[11px] uppercase tracking-wider" style={{ color: 'var(--ink-tertiary)' }}>
                {user.role}
              </span>
            </div>
          )}
          <Button variant="ghost" size="sm" onClick={handleSignOut}>
            <LogOut size={14} aria-hidden />
            Sign out
          </Button>
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 overflow-auto" style={{ background: 'var(--bg-base)' }}>
        <div className="mx-auto max-w-7xl p-6">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
